import { useEffect, useRef, useState } from 'react';
import { Canvg } from 'canvg';

export type TemplateComponentProps = {
  qrCodeSvg: string;
  size: number;
  textClassname?: string;
  refId: number;
  onRenderReady?: (isReady: boolean) => void;
};

export const Template2_4x6 = ({ qrCodeSvg, size, refId, onRenderReady }: TemplateComponentProps) => (
  <TemplateComponent
    count={2}
    wrapperClassname=" w-[8.5in] h-[11in] grid grid-cols-1 grid-rows-2 p-[0.625in] "
    itemClassname="px-2 flex items-center justify-between border border-gray-300 text-lg text-center w-[6in] h-[4in] mx-auto"
    size={size}
    textClassname="text-2xl font-semibold leading-[140%]"
    qrCodeSvg={qrCodeSvg}
    refId={refId}
    onRenderReady={onRenderReady}
  />
);

export const Template24_3x8_1x2 = ({ qrCodeSvg, size, refId, onRenderReady }: TemplateComponentProps) => (
  <TemplateComponent
    count={24}
    wrapperClassname=" w-[8.5in] h-[11in] p-[0.625in] flex flex-wrap justify-center gap-x-[0.625in] gap-y-[0.125in]"
    itemClassname="px-2 w-[2in] h-[1in] border flex items-center justify-between text-xs"
    size={size}
    refId={refId}
    textClassname="font-semibold leading-[140%]"
    qrCodeSvg={qrCodeSvg}
    onRenderReady={onRenderReady}
  />
);

export const Template1_3x1 = ({ qrCodeSvg, size, refId, onRenderReady }: TemplateComponentProps) => (
  <TemplateComponent
    count={1}
    wrapperClassname="w-[3in] h-[1in]"
    itemClassname="w-[3in] h-[1in] px-[0.08in] py-[0.05in] border flex items-center justify-between gap-[0.08in] text-[10px]"
    size={size}
    refId={refId}
    textClassname="text-[11px] font-semibold leading-tight"
    textWrapperClassname="flex flex-col items-end justify-center gap-[2px] shrink-0"
    qrCodeSvg={qrCodeSvg}
    onRenderReady={onRenderReady}
  />
);

const TemplateComponent = ({
  qrCodeSvg,
  size,
  count,
  wrapperClassname,
  textClassname,
  itemClassname,
  textWrapperClassname = 'flex flex-col items-end gap-4',
  refId,
  onRenderReady,
}: TemplateComponentProps & {
  count: number;
  wrapperClassname: string;
  itemClassname: string;
  textWrapperClassname?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrCodePngUrl, setQrCodePngUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const renderQrCode = async () => {
      onRenderReady?.(false);
      setQrCodePngUrl(null);

      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      // Render at a high internal resolution to keep small QR codes crisp in the PDF.
      const upscale = 6;
      const renderWidth = size * upscale;
      const renderHeight = size * upscale;

      canvas.width = renderWidth;
      canvas.height = renderHeight;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.setTransform(upscale, 0, 0, upscale, 0, 0);

      const renderer = Canvg.fromString(ctx, qrCodeSvg);
      await renderer.render();

      if (!isMounted) {
        return;
      }

      setQrCodePngUrl(canvas.toDataURL('image/png'));
      onRenderReady?.(true);
    };

    void renderQrCode();

    return () => {
      isMounted = false;
      onRenderReady?.(false);
    };
  }, [onRenderReady, qrCodeSvg, size]);

  return (
    <>
      <canvas className="fixed top-full left-full" ref={canvasRef} />
      <div className={wrapperClassname}>
        {Array.from({ length: count }, (_, idx) => (
          <div className={itemClassname} key={idx}>
            {qrCodePngUrl && <img height={size} width={size} src={qrCodePngUrl} alt="SVG QR" />}
            <div className={textWrapperClassname}>
              <span className={textClassname}>AssessPro</span>
              <span className={textClassname}>#{refId}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
